import {Box, Card, CardContent, Stack, SxProps, Typography} from '@mui/material';
import {Role} from '../../backend/interfaces/role';
import {getLocalizedFieldValue} from '../../utils/functions/get-localized-field-value';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {ComboActions} from '../combo-actions/combo-actions';
import {FiEdit, FiTrash2} from 'react-icons/fi';
import {Pallete} from '../../theme/bluebird';

const cardSx: SxProps = {
  width: '100%',
  borderRadius: '8px',
  boxShadow: '0px -3px 14px 0px rgba(29, 41, 57, 0.15)',
  '&:hover': {
    backgroundColor: 'var(--dcm-blue-hover)',
  },
};

const cardContentSx: SxProps = {
  position: 'relative',
  padding: '24px',
};

const contentBoxWrapperSx: SxProps = {
  position: 'relative',
  width: '100%',
  paddingRight: '32px',
};

const comboActionsSx: SxProps = {
  color: Pallete.gray.light,
  display: 'flex',
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'flex-end',
  position: 'absolute',
  right: 0,
  top: 0,
};

const titleWrapperSx: SxProps = {
  display: 'flex',
  padding: '4px 0px',
  alignItems: 'center',
  gap: '7px',
  height: '18px',
};

const titleSx: SxProps = {
  fontSize: '18px',
  fontWeight: 700,
};

const defaultRoleSx: SxProps = {
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '18px',
  color: 'var(--dcm-blue-base)',
  marginLeft: '12px',
};

const descriptionSx: SxProps = {
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '18px',
  marginTop: '20px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '2',
  WebkitBoxOrient: 'vertical',
};

interface RoleCardProps {
  role: Role;
  actionsEnabled: boolean;
  onEditRoleClick: () => void;
  onDeleteRoleClick: () => void;
}

const RoleCard = ({role, actionsEnabled, onEditRoleClick, onDeleteRoleClick}: RoleCardProps) => {
  const {t} = useTranslation();
  const description = getLocalizedFieldValue(role, 'description');

  const defaultRole = !role.entityId;
  const textColor = defaultRole ? 'var(--dcm-gray-input)' : 'var(--dcm-gray)';

  return (
    <Card sx={cardSx}>
      <CardContent sx={cardContentSx} component={Stack} direction="row">
        <Box sx={contentBoxWrapperSx}>
          <Stack direction="column" flexGrow={1}>
            <Box sx={titleWrapperSx}>
              <Typography sx={{...titleSx, color: textColor}} noWrap>
                {getLocalizedFieldValue(role, 'name')}
                {defaultRole && (
                  <Typography component="span" sx={defaultRoleSx}>
                    {t(
                      translations.office.professional_caregiver.roles_and_permissions.default_role
                    )}
                  </Typography>
                )}
              </Typography>
            </Box>
            <Typography sx={{...descriptionSx, color: textColor}}>{description}</Typography>
          </Stack>
          {!defaultRole && actionsEnabled && (
            <ComboActions
              wrapperSX={comboActionsSx}
              triggerSX={{height: '32px', width: '32px'}}
              actions={[
                {
                  label: t(translations.office.professional_caregiver.roles_and_permissions.edit),
                  icon: {element: FiEdit},
                  callback: onEditRoleClick,
                },
                {
                  label: t(translations.office.professional_caregiver.roles_and_permissions.delete),
                  icon: {element: FiTrash2},
                  callback: onDeleteRoleClick,
                },
              ]}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default RoleCard;
